import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import authService from '../services/auth.service';
import { UserAccount } from '../types/user';

interface AuthState {
  user: UserAccount | null;
  isAuthenticated: boolean;
  loading: boolean;
  hasCheckedAuth: boolean;
  login: (user: UserAccount) => void;
  logout: () => void;
  getMe: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      loading: false,
      hasCheckedAuth: false,
      login: (user) =>
        set({
          user,
          isAuthenticated: true,
          loading: false,
          hasCheckedAuth: true,
        }),
      logout: () =>
        set({
          user: null,
          isAuthenticated: false,
          loading: false,
        }),
      getMe: async () => {
        set({ loading: true });
        try {
          const user = await authService.getMe();
          set({
            user,
            isAuthenticated: true,
            loading: false,
            hasCheckedAuth: true,
          });
          // No proactive refresh-timer re-scheduling here: the access token
          // lives only in an httpOnly cookie, unreadable from JS, so its
          // expiry can't be decoded client-side after a page reload. The
          // reactive JWT_EXPIRED retry in axios-instance's responseError
          // covers this case — the next request past expiry silently
          // refreshes and retries.
        } catch (error) {
          console.error('Error fetching user:', error);
          const status = (error as any)?.response?.status ?? (error as any)?.status;
          const isAuthError = status === 401 || status === 403;
          if (isAuthError) {
            set({ user: null, isAuthenticated: false, loading: false, hasCheckedAuth: true });
          } else {
            // Non-auth errors (network, 5xx) should not log the user out
            set({ loading: false, hasCheckedAuth: true });
          }
        }
      },
    }),
    {
      name: 'auth-storage',
      // localStorage (not sessionStorage) — shared across tabs of the same
      // origin, so opening an app tile in a new tab (or typing the URL
      // directly) keeps the session instead of bouncing to /login.
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
